import { useEffect, useMemo, useState } from 'react'
import type { FormInstance } from 'antd/es/form/Form'
import {
  discountModeToApplyEachPayment,
  resolveCrmLinkDiscountMode,
  type CrmLinkDiscountMode,
} from '../../discountMode.utils'
import {
  getClassLinkBasePrice,
  getClassLinkPeriodBasePrice,
  getClassLinkSuggestedPrice,
  getMonthlyCourseLinkBasePrice,
  getMonthlyCourseLinkPeriodBasePrice,
  getMonthlyCourseLinkSuggestedPrice,
  getMonthlyPeriodToggleResetValues,
  isMonthlyCourseLinkTariff,
  type MonthlyCourseLinkPeriodUnit,
} from './monthlyCourseLink.utils'

type UseMonthlyCourseLinkFormParams = {
  form: FormInstance
  typeWatch: string | null | undefined
  selectedTariff?: ICourseTariff | null
  selectedClass?: {
    amount?: unknown
    one_pay?: boolean | number | null
  } | null
  valueWatch: number | null
  periodUnitWatch: MonthlyCourseLinkPeriodUnit
  discountWatch: number | null
  discountModeWatch: CrmLinkDiscountMode | null
  enrollDateWatch: string | null
  applyDiscountEachPaymentWatch: boolean
  isPeriodFixed: boolean
  setIsPeriodFixed: (value: boolean) => void
}

/**
 * Оркестрация помесячного тарифа и class one_pay в create-link:
 * цены с discount_mode, автосинк price, toggle фиксации периода.
 */
export const useMonthlyCourseLinkForm = ({
  form,
  typeWatch,
  selectedTariff,
  selectedClass,
  valueWatch,
  periodUnitWatch,
  discountWatch,
  discountModeWatch,
  enrollDateWatch,
  applyDiscountEachPaymentWatch,
  isPeriodFixed,
  setIsPeriodFixed,
}: UseMonthlyCourseLinkFormParams) => {
  const [isPriceManuallyEdited, setIsPriceManuallyEdited] = useState(false)

  const isMonthlyCourseLink =
    typeWatch === 'course' && isMonthlyCourseLinkTariff(selectedTariff?.type)
  const isClassPaymentLink =
    typeWatch === 'class' && Boolean(selectedClass?.one_pay)
  const isActive = isMonthlyCourseLink || isClassPaymentLink

  const monthlyCourseBasePrice = getMonthlyCourseLinkBasePrice(
    selectedTariff?.first_price,
  )
  const classBasePrice = getClassLinkBasePrice(selectedClass?.amount)

  const resolvedDiscountMode = resolveCrmLinkDiscountMode({
    discount: discountWatch,
    discountMode: discountModeWatch,
    applyDiscountEachPayment: applyDiscountEachPaymentWatch,
    isPeriodFixed,
  })

  const monthlyFullBasePrice = useMemo(
    () =>
      getMonthlyCourseLinkPeriodBasePrice(
        selectedTariff,
        periodUnitWatch,
        valueWatch ?? 1,
        enrollDateWatch,
      ),
    [enrollDateWatch, periodUnitWatch, selectedTariff, valueWatch],
  )

  const classFullBasePrice = useMemo(
    () =>
      getClassLinkPeriodBasePrice(
        selectedClass?.amount,
        periodUnitWatch,
        valueWatch ?? 1,
        enrollDateWatch,
      ),
    [enrollDateWatch, periodUnitWatch, selectedClass?.amount, valueWatch],
  )

  const fullBasePrice = isClassPaymentLink
    ? classFullBasePrice
    : monthlyFullBasePrice

  const monthlySuggestedPrice = useMemo(
    () =>
      getMonthlyCourseLinkSuggestedPrice(
        selectedTariff,
        periodUnitWatch,
        valueWatch ?? 1,
        discountWatch,
        enrollDateWatch,
        applyDiscountEachPaymentWatch,
        discountModeWatch,
        isPeriodFixed,
      ),
    [
      applyDiscountEachPaymentWatch,
      discountModeWatch,
      discountWatch,
      enrollDateWatch,
      isPeriodFixed,
      periodUnitWatch,
      selectedTariff,
      valueWatch,
    ],
  )

  const classSuggestedPrice = useMemo(
    () =>
      getClassLinkSuggestedPrice(
        selectedClass?.amount,
        periodUnitWatch,
        valueWatch ?? 1,
        discountWatch,
        enrollDateWatch,
        applyDiscountEachPaymentWatch,
        discountModeWatch,
        isPeriodFixed,
      ),
    [
      applyDiscountEachPaymentWatch,
      discountModeWatch,
      discountWatch,
      enrollDateWatch,
      isPeriodFixed,
      periodUnitWatch,
      selectedClass?.amount,
      valueWatch,
    ],
  )

  const suggestedPrice = isClassPaymentLink
    ? classSuggestedPrice
    : monthlySuggestedPrice

  useEffect(() => {
    if (!isActive) return
    // Без фиксации периода цена всегда расчётная.
    // При фиксации — можно править вручную (пока не «вернуть расчётную»).
    if (!isPeriodFixed) {
      setIsPriceManuallyEdited(false)
      form.setFieldValue('price', suggestedPrice)
      return
    }
    if (isPriceManuallyEdited) return

    form.setFieldValue('price', suggestedPrice)
  }, [form, isActive, isPeriodFixed, isPriceManuallyEdited, suggestedPrice])

  const onTogglePeriod = (checked?: boolean) => {
    const isChecked =
      typeof checked === 'boolean' ? checked : !isPeriodFixed

    setIsPeriodFixed(isChecked)
    setIsPriceManuallyEdited(false)

    const hasDiscount = Number(discountWatch) > 0
    const nextMode: CrmLinkDiscountMode | null = hasDiscount
      ? isChecked
        ? 'renewal_only'
        : 'first_only'
      : null

    form.setFieldsValue({
      ...getMonthlyPeriodToggleResetValues(valueWatch),
      discount_mode: nextMode,
      apply_discount_each_payment: discountModeToApplyEachPayment(nextMode),
    })
  }

  const onPriceManualChange = () => {
    if (!isPeriodFixed) return
    setIsPriceManuallyEdited(true)
  }

  const onResetSuggestedPrice = () => {
    setIsPriceManuallyEdited(false)
    form.setFieldValue('price', suggestedPrice)
  }

  const onDiscountModeChange = (mode: CrmLinkDiscountMode | null) => {
    const nextMode = resolveCrmLinkDiscountMode({
      discount: discountWatch,
      discountMode: mode,
      isPeriodFixed,
    })
    form.setFieldsValue({
      discount_mode: nextMode,
      apply_discount_each_payment: discountModeToApplyEachPayment(nextMode),
    })
  }

  const resetManualPriceAndPeriod = () => {
    setIsPriceManuallyEdited(false)
    setIsPeriodFixed(false)
  }

  return {
    isActive,
    isMonthlyCourseLink,
    isClassPaymentLink,
    isPeriodFixed,
    isPriceManuallyEdited,
    monthlyCourseBasePrice,
    classBasePrice,
    fullBasePrice,
    monthlySuggestedPrice,
    classSuggestedPrice,
    suggestedPrice,
    discountMode: resolvedDiscountMode,
    onTogglePeriod,
    onPriceManualChange,
    onResetSuggestedPrice,
    onDiscountModeChange,
    resetManualPriceAndPeriod,
    setIsPriceManuallyEdited,
  }
}
